// /competitors/:id — single-competitor detail page. Replaces the
// previous "View ads" modal so the surface is shareable, has a back
// button, and can grow with more per-competitor controls (rename,
// notes, refresh schedule, etc.) without a fight with modal sizing.
//
// Data: loads competitor metadata + cached ads in parallel. Refresh
// button bumps the same backend endpoint as the list page.

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AppLayoutV2 from '@/components/feature/AppLayoutV2';
import {
  deleteCompetitor,
  getCompetitorAds,
  listCompetitors,
  refreshCompetitor,
  type GatheredAd,
  type SavedCompetitor,
} from '@/lib/api';
import { useConfirm } from '@/hooks/useConfirm';
import { useBrand } from '@/hooks/useBrand';
import { useCredits } from '@/hooks/useCredits';

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return 'Never';
  const then = new Date(iso).getTime();
  const ms = Math.max(0, Date.now() - then);
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
}

export default function CompetitorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { activeBrand } = useBrand();
  // Refresh is a Pro action (each is a paid Apify pull); free/starter view the
  // cached ads read-only. Backend enforces the same gate.
  const { planTier } = useCredits();
  const isPro = planTier === 'pro' || planTier === 'business';

  const [competitor, setCompetitor] = useState<SavedCompetitor | null>(null);
  const [ads, setAds] = useState<GatheredAd[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      // Two parallel calls: metadata via listCompetitors (we filter to
      // this id) + cached ads via getCompetitorAds. The metadata fetch
      // is cheap since the user is unlikely to have hundreds of saved
      // competitors; a dedicated GET /api/competitors/:id is a nice-to-
      // have but not worth a round-trip today.
      const [listResult, adsResult] = await Promise.all([
        listCompetitors(),
        getCompetitorAds(id),
      ]);
      const found = listResult.competitors.find((c) => c.id === id) ?? null;
      setCompetitor(found);
      setAds(adsResult.ads);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load competitor');
    } finally {
      setLoading(false);
    }
  }, [id, activeBrand?.id]);

  useEffect(() => { void load(); }, [load]);

  const handleRefresh = async () => {
    if (!id) return;
    setRefreshing(true);
    setError(null);
    try {
      const result = await refreshCompetitor(id);
      setCompetitor(result.competitor);
      // Re-fetch ads since cache was cleared + repopulated.
      const adsResult = await getCompetitorAds(id);
      setAds(adsResult.ads);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const handleDelete = async () => {
    if (!competitor) return;
    const ok = await confirm({
      title: `Remove ${competitor.label}?`,
      message: 'This stops tracking them. You can add them back any time.',
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    try {
      await deleteCompetitor(competitor.id);
      navigate('/competitors');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  // Group ads: with-image first (most actionable), then text-only at the
  // bottom (still useful for copy inspiration).
  const adsWithImage = ads.filter((a) => a.media_cached_url || a.media_ref);
  const adsTextOnly = ads.filter((a) => !a.media_cached_url && !a.media_ref);

  return (
    <AppLayoutV2>
      <div className="max-w-[1500px] mx-auto px-6 py-8">
        {/* Back link */}
        <Link
          to="/competitors"
          className="inline-flex items-center gap-1 text-xs font-bold text-dark-muted hover:text-primary mb-3 cursor-pointer"
        >
          <i className="ri-arrow-left-s-line" /> Back to Competitors
        </Link>

        {/* Header */}
        {loading && !competitor ? (
          <div className="flex items-center gap-2 py-10 text-dark-muted">
            <i className="ri-loader-4-line animate-spin text-2xl" />
            Loading competitor…
          </div>
        ) : !competitor ? (
          <div className="rounded-2xl glass-stroke bg-white/85 px-6 py-12 text-center">
            <i className="ri-error-warning-line text-3xl text-dark-muted/60" />
            <p className="font-display font-bold text-dark text-base mt-3">Competitor not found</p>
            <p className="text-sm text-dark-muted mt-1">
              It may have been removed. Head back to the list to add it again.
            </p>
            <Link
              to="/competitors"
              className="mt-4 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-line" /> Back to Competitors
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h1 className="font-display font-bold text-dark text-2xl truncate">
                    {competitor.label}
                  </h1>
                  <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${
                    competitor.active_ads_count > 0
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-surface-dark/30 text-dark-muted'
                  }`}>
                    {competitor.active_ads_count > 0
                      ? `${competitor.active_ads_count}${competitor.active_ads_count >= 20 ? '+' : ''} active ad${competitor.active_ads_count === 1 ? '' : 's'}`
                      : 'No active ads'}
                  </span>
                </div>
                {competitor.facebook_handle && (
                  <p className="text-sm text-dark-muted">
                    <i className="ri-facebook-circle-line mr-1" />
                    <a
                      href={competitor.query}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-primary hover:underline"
                    >
                      facebook.com/{competitor.facebook_handle}
                    </a>
                  </p>
                )}
                <p className="text-xs text-dark-muted mt-1">
                  <i className="ri-time-line mr-1" />
                  Refreshed {relativeTime(competitor.last_refreshed_at)}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => (isPro ? handleRefresh() : navigate('/checkout'))}
                  disabled={refreshing}
                  title={!isPro ? 'Refreshing competitor ads is a Pro feature' : undefined}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-primary hover:bg-primary-hover text-white text-xs font-bold shadow-md shadow-primary/25 transition-all cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {refreshing ? (
                    <><i className="ri-loader-4-line animate-spin" /> Refreshing…</>
                  ) : !isPro ? (
                    <><i className="ri-vip-crown-line" /> Refresh on Pro</>
                  ) : (
                    <><i className="ri-refresh-line" /> Refresh ads</>
                  )}
                </button>
                <button
                  onClick={handleDelete}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-red-200 text-red-600 hover:bg-red-50 text-xs font-bold transition-all cursor-pointer"
                >
                  <i className="ri-delete-bin-line" /> Remove
                </button>
              </div>
            </div>

            {error && (
              <div className="mb-4 flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 text-red-700 border border-red-200 text-sm font-medium">
                <i className="ri-error-warning-line" />
                {error}
              </div>
            )}

            {loading ? (
              <div className="flex items-center gap-2 py-10 text-dark-muted">
                <i className="ri-loader-4-line animate-spin text-2xl" />
                Loading ads…
              </div>
            ) : ads.length === 0 ? (
              <div className="rounded-2xl glass-stroke bg-white/85 px-6 py-12 text-center">
                <i className="ri-image-line text-3xl text-dark-muted/60" />
                <p className="font-display font-bold text-dark text-base mt-3">No ads cached yet</p>
                <p className="text-sm text-dark-muted mt-1">
                  Click <strong>Refresh ads</strong> above to pull the latest from the Meta Ad Library.
                </p>
              </div>
            ) : (
              <>
                {/* Ads with visual creative — the main grid */}
                {adsWithImage.length > 0 && (
                  <>
                    <h2 className="text-xs font-bold text-dark-muted uppercase tracking-wide mb-3">
                      Active ads · {adsWithImage.length}
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4 mb-8">
                      {adsWithImage.map((ad, i) => (
                        <AdCard key={ad.ad_archive_id || `ad-${i}`} ad={ad} />
                      ))}
                    </div>
                  </>
                )}

                {/* Text-only ads — copy-inspiration section */}
                {adsTextOnly.length > 0 && (
                  <>
                    <h2 className="text-xs font-bold text-dark-muted uppercase tracking-wide mb-3">
                      Text-only ads · {adsTextOnly.length}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                      {adsTextOnly.map((ad, i) => (
                        <TextAdCard key={ad.ad_archive_id || `text-ad-${i}`} ad={ad} />
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </AppLayoutV2>
  );
}

// ----- Ad card with image -----

function AdCard({ ad }: { ad: GatheredAd }) {
  const [imgFailed, setImgFailed] = useState(false);
  // Fallback chain: media_cached_url (our Storage thumbnail) → media_ref
  // (original fbcdn URL — may have expired but worth trying). Defensive
  // since vendor URLs are advertised as ephemeral.
  const primaryUrl = ad.media_cached_url || ad.media_ref;
  const fallbackUrl = ad.media_cached_url && ad.media_ref && !imgFailed
    ? ad.media_ref
    : null;
  const displayUrl = imgFailed && fallbackUrl ? fallbackUrl : primaryUrl;

  return (
    <div className="rounded-xl bg-white border border-surface-dark/30 overflow-hidden hover:shadow-md hover:border-primary/40 transition-all">
      <div className="relative aspect-square bg-surface-dark/15">
        {displayUrl ? (
          <img
            src={displayUrl}
            alt={ad.headline || ad.ad_text?.slice(0, 60) || 'Ad creative'}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            decoding="async"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/5 to-accent/5">
            <i className="ri-image-line text-3xl text-dark-muted/40" />
          </div>
        )}
        {ad.longevity_days != null && ad.longevity_days >= 14 && (
          <span className="absolute top-2 left-2 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-500 text-white">
            {ad.longevity_days}d running
          </span>
        )}
      </div>
      <div className="p-3">
        {ad.headline && (
          <p className="font-bold text-dark text-sm line-clamp-2 mb-1">{ad.headline}</p>
        )}
        {ad.ad_text && (
          <p className="text-xs text-dark-muted line-clamp-3 mb-2">{ad.ad_text}</p>
        )}
        <div className="flex items-center gap-1.5 flex-wrap">
          {ad.cta && (
            <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-bold">
              {ad.cta}
            </span>
          )}
          {ad.variations != null && ad.variations > 1 && (
            <span className="px-1.5 py-0.5 rounded bg-surface-dark/30 text-dark-muted text-[10px] font-bold">
              {ad.variations} variants
            </span>
          )}
          {import.meta.env.DEV && ad.ad_archive_id && (
            <a
              href={`https://www.facebook.com/ads/library/?id=${ad.ad_archive_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto text-[10px] font-bold text-dark-muted hover:text-primary"
              title="View original on Meta Ad Library"
            >
              <i className="ri-external-link-line" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// ----- Text-only ad (no media) -----

function TextAdCard({ ad }: { ad: GatheredAd }) {
  return (
    <div className="rounded-xl bg-white border border-surface-dark/30 p-4 hover:shadow-md hover:border-primary/40 transition-all">
      {ad.headline && (
        <p className="font-bold text-dark text-base mb-2">{ad.headline}</p>
      )}
      {ad.ad_text && (
        <p className="text-sm text-dark-muted whitespace-pre-wrap line-clamp-6">{ad.ad_text}</p>
      )}
      <div className="flex items-center gap-1.5 flex-wrap mt-3">
        {ad.cta && (
          <span className="px-2 py-0.5 rounded bg-primary/10 text-primary text-xs font-bold">
            {ad.cta}
          </span>
        )}
        {ad.longevity_days != null && ad.longevity_days >= 14 && (
          <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 text-xs font-bold">
            {ad.longevity_days}d running
          </span>
        )}
        {ad.variations != null && ad.variations > 1 && (
          <span className="px-2 py-0.5 rounded bg-surface-dark/30 text-dark-muted text-xs font-bold">
            {ad.variations} variants
          </span>
        )}
        {import.meta.env.DEV && ad.ad_archive_id && (
          <a
            href={`https://www.facebook.com/ads/library/?id=${ad.ad_archive_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto text-xs font-bold text-dark-muted hover:text-primary"
            title="View original on Meta Ad Library"
          >
            <i className="ri-external-link-line" /> Meta Ad Library
          </a>
        )}
      </div>
    </div>
  );
}
